import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useQueryClient, QueryKey } from 'react-query';
import { User } from '../types/user';
import { disconnectSocket } from '../services/socketService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (userData: any) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message?: string }>;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ success: false }),
  register: async () => ({ success: false }),
  logout: () => {},
  forgotPassword: async () => ({ success: false }),
  resetPassword: async () => ({ success: false }),
  refreshUserData: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const userMeQueryKey: QueryKey = ['user', 'me'];
const userLocationQueryKey: QueryKey = 'userLocation';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const queryClient = useQueryClient();

  const checkAuth = useCallback(async (isInitialLoad = false) => {
    if (isInitialLoad) {
        setIsLoading(true);
    }
    let authenticated = false;
    let userData: User | null = null;
    const authToken = localStorage.getItem('token');

    if (authToken) {
        try {
            const response = await api.get('/users/me');
            if (response.data.success && response.data.user) {
                 authenticated = true;
                 userData = response.data.user as User;
            } else {
                 console.warn('[AuthContext] checkAuth: /users/me success but user data missing or success=false. Removing token.');
                 localStorage.removeItem('token');
            }
        } catch (error: any) {
            console.error('[AuthContext] checkAuth: Error during token verification (/users/me):', error?.response?.data?.message ?? error?.message ?? 'Unknown error');
            localStorage.removeItem('token');
        }
    }

    setUser(userData);
    setIsAuthenticated(authenticated);

    setIsLoading(false);
  }, []);

  useEffect(() => {
    let isMounted = true;
    console.log('[AuthContext] useEffect: Starting initial auth check...');

    checkAuth(true);

    return () => {
      console.log('[AuthContext] useEffect: Cleanup.');
      isMounted = false;
    };
  }, [checkAuth]);

  const refreshUserData = useCallback(async () => {
    await checkAuth(false);
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      
      if (response.data.success && response.data.token && response.data.user) {
        const loggedInUser: User = response.data.user;
        const token = response.data.token;
        
        localStorage.setItem('token', token);
        setUser(loggedInUser);
        setIsAuthenticated(true);
        
        queryClient.refetchQueries(userMeQueryKey, { exact: true });
        queryClient.refetchQueries(userLocationQueryKey, { exact: true });
        
        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Login failed: Invalid server response' };
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'An error occurred during login.';
      return { success: false, message };
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await api.post('/auth/register', userData);
      
      if (response.data.success && response.data.token && response.data.user) {
        const loggedInUser: User = response.data.user;
        const token = response.data.token;

        localStorage.setItem('token', token);
        setUser(loggedInUser);
        setIsAuthenticated(true);

        await Promise.all([
          queryClient.refetchQueries(userMeQueryKey, { exact: true }),
          queryClient.refetchQueries(userLocationQueryKey, { exact: true }),
          queryClient.refetchQueries('allUsers')
        ]);

        return { success: true };
      } else {
        return { success: false, message: response.data.message || 'Registration failed: Invalid server response' };
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred during registration.';
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    disconnectSocket();
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    
    queryClient.removeQueries(userMeQueryKey, { exact: true });
    queryClient.removeQueries(userLocationQueryKey, { exact: true });
    queryClient.removeQueries('allUsers');
    queryClient.removeQueries('conversations');
    queryClient.removeQueries('messages');
    queryClient.removeQueries('connections');
    queryClient.removeQueries('pendingRequests');
  };

  const forgotPassword = async (email: string) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message || 'Password reset request failed' };
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
      return { success: false, message: errorMessage };
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      const response = await api.post(`/auth/reset-password/${token}`, { password });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message || 'Password reset failed' };
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'An error occurred. Please try again.';
      return { success: false, message: errorMessage };
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
